import { useRef, useState } from "react";
import axios from "axios";
import { config } from "modules/ai-quote/config";

const maxFileSize = 15 * 1024 * 1024;

const UploadArea = ({ files, setFiles }) => {
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [exceedsLimit, setExceedsLimit] = useState(false);
  const [uploadStatus, setUploadStatus] = useState([]);
  const [currentUploadIndex, setCurrentUploadIndex] = useState(0);

  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  const isValidFileType = (file) => {
    const validTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "application/pdf",
    ];
    return validTypes.includes(file.type);
  };

  const uploadFileToAPI = async (file) => {
    const formData = new FormData();
    formData.append("files", file);

    try {
      const response = await axios.post(
        `${config.BASE_API_URL}/api/v1/clients/file-upload`,

        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return {
        originalName: decodeURIComponent(escape(response.data[0].originalName)),
        path: response.data[0].path,
      };
    } catch (error) {
      throw new Error("File upload failed");
    }
  };

  const handleFileChange = async (event) => {
    const newFiles = Array.from(event.target.files || []).filter(
      isValidFileType
    );

    for (let i = 0; i < newFiles.length; i++) {
      const file = newFiles[i];

      if (file.size > maxFileSize) {
        setExceedsLimit(true);
        return;
      }

      setCurrentUploadIndex(i + 1);
      setUploadStatus((prevStatus) => [
        ...prevStatus,
        { name: file.name, status: "uploading" },
      ]);

      try {
        const uploadedFile = await uploadFileToAPI(file);

        setFiles((prevFiles) => [...prevFiles, uploadedFile]);
        setUploadStatus((prevStatus) =>
          prevStatus.map((status) =>
            status.name === file.name
              ? { ...status, status: "uploaded" }
              : status
          )
        );
      } catch (error) {
        setUploadStatus((prevStatus) =>
          prevStatus.map((status) =>
            status.name === file.name ? { ...status, status: "failed" } : status
          )
        );
      }
    }

    setExceedsLimit(false);
    setCurrentUploadIndex(0);
  };

  const handleDragEnter = (event) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(event.dataTransfer.files).filter(
      isValidFileType
    );

    for (let i = 0; i < droppedFiles.length; i++) {
      const file = droppedFiles[i];

      if (file.size > maxFileSize) {
        setExceedsLimit(true);
        return;
      }
    }

    setExceedsLimit(false);
    handleFileChange({ target: { files: droppedFiles } });
  };

  const handleRetry = () => {
    setExceedsLimit(false);

    const failedFiles = uploadStatus.filter(
      (status) => status.status === "failed"
    );
    const validFiles = files.filter(
      (file) => !failedFiles.some((failed) => failed.name === file.name)
    );

    setFiles(validFiles);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="app-ai-quote-upload-area">
      <div
        className={`app-ai-quote-dnd-area ${
          isDragging ? "app-ai-quote-dragging" : ""
        } ${exceedsLimit ? "app-ai-quote-dnd-error-area" : ""}`}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {exceedsLimit ? (
          <div className={"app-ai-quote-dnd-error-icon"}>
            <img
              src="/order-now/icons/icon-error-dnd.svg"
              width={24}
              height={24}
              alt="Error icon"
            />
          </div>
        ) : currentUploadIndex > 0 ? (
          <div className="app-ai-quote-upload-progress app-ai-quote-upload-progress-upload">
            <div class="app-ai-quote-file-upload-loader" />
            <p>Uploading file.</p>
          </div>
        ) : isDragging ? (
          <div className="app-ai-quote-dnd-drop-icon">
            <img
              src="/order-now/icons/icon-arrow-top-dnd.svg"
              width={24}
              height={24}
              alt="Drop icon"
            />
          </div>
        ) : (
          <div className="app-ai-quote-dnd-upload-icon">
            <img
              src="/order-now/icons/icon-upload-dnd.svg"
              width={24}
              height={24}
              alt="Upload icon"
            />
          </div>
        )}
        {exceedsLimit ? (
          <div className="app-ai-quote-dnd-error-message">
            <p>
              Upload Failed! Please{" "}
              <button onClick={handleRetry}>try again</button>.
            </p>
            <p>15MB max file size</p>
          </div>
        ) : (
          <p>
            Drop files here to upload or
            <input
              type="file"
              ref={fileInputRef}
              accept=".png, .jpg, .jpeg, .pdf"
              multiple
              onChange={handleFileChange}
            />{" "}
            <span
              onClick={handleFileClick}
              className={isDragging ? "app-ai-quote-dnd-dragging" : ""}
            >
              Choose File
            </span>
          </p>
        )}
      </div>
      <div className="app-ai-quote-dnd-file-breadcrumbs">
        {files.map((file, index) => (
          <span key={index}>
            {file.originalName || file.name}
            <button
              onClick={() => {
                const curFiles = files.filter((_, i) => i !== index);
                setFiles(curFiles);

                if (fileInputRef && fileInputRef.current) {
                  fileInputRef.current.value = "";
                }
              }}
            >
              <img
                src="/order-now/icons/icon-close-dnd.svg"
                width={16}
                height={16}
                alt="Close"
              />
            </button>
            <span>
              {uploadStatus.find((status) => status.name === file.name)
                ?.status === "failed"
                ? " (Failed)"
                : ""}
            </span>
          </span>
        ))}
      </div>
    </div>
  );
};

export default UploadArea;
